import React from 'react';
import { Button, Select, Card, Space, Mentions, Flex } from 'antd';
import { PlusOutlined, DeleteOutlined, ManOutlined, UserOutlined, PhoneOutlined, MailOutlined, FileTextOutlined, RobotOutlined, BankOutlined, TeamOutlined, WomanOutlined } from '@ant-design/icons';
import './ScenarioSamplesEditor.css';
import { ScenarioSample } from '../../../../domain/models/scenario/ScenarioSample';
import { JobType, jobTypeAsString } from "@leedpulse-monorepo/enums";
import classNames from 'classnames';
import { CustomSelect } from '@leedpulse-monorepo/components';

const { Option } = Select;

interface ScenarioSamplesEditorProps {
  samples: ScenarioSample[];
  isBusy: boolean;
  onChange: (newSamples: ScenarioSample[]) => void;
  disabled?: boolean;
}

// Base samples context for basis situations
const base_samples_context = [
  "Le client répond négativement", 
  "Le client répond positivement", 
  "Le client ne répond pas", 
  "Le client demande un appel téléphonique", 
  "Le client pense qu'il s'agit d'une arnaque",
  "Le client ne comprend pas le message",
  "Le client ne veut pas être contacté",
  "Le client s'impatiente", 
];

const ScenarioSamplesEditor: React.FC<ScenarioSamplesEditorProps> = ({
  samples,
  isBusy,
  onChange,
  disabled,
}) => {
  // One update function to rule them all
  const handleChange = (index:number, changes: Partial<ScenarioSample>) => {
    const updated = [...samples];
    const sample = updated[index];
    updated[index] = sample.copyWith(changes);
    onChange(updated);
  };

  const handleDelete = (index: number) => {
    const updated = [...samples];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleAdd = () => {
    const newSample = new ScenarioSample(JobType.reply, '');
    const updated = [...samples, newSample];
    onChange(updated);
  };

  return (
    <div
      className={classNames('scenario-sample-editor', {
        'scenario-sample-editor--disabled': disabled,
        'scenario-sample-editor--busy': isBusy,
      })}
    >
      <div className="samples-list">
        {samples.map((sample, index) => (
          <Card
            key={index}
            className="sample-card"
            size="small"
            title={`Modèle n°${index + 1}`}
            extra={
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(index)}
                disabled={isBusy || disabled}
              />
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Select
                value={sample.type}
                onChange={(value: JobType) => handleChange(index, { type: value })}
                disabled={isBusy || disabled}
                style={{ width: '100%' }}
              >
                {Object.values(JobType).filter((type) => type !== JobType.workflow).map((type) => (
                  <Option key={type} value={type}>
                    {jobTypeAsString(type)}
                  </Option>
                ))}
              </Select>
              <CustomSelect
                showSearch
                options={base_samples_context.map((context) => ({
                  value: context,
                  label: context,
                }))}
                maxLength={128}
                defaultValue={sample.context}
                onChange={(e) => {
                  handleChange(index, { context: e as string });
                }}
                notFoundContent={<>Le contexte n'a pas été trouvé</>}
                placeholder="Contexte d'utilisation. Ex: Le client répond négativement"
                disabled={isBusy || disabled}
                allowClear
              />
              <Mentions
                maxLength={500}
                value={sample.message}
                prefix={['#']}
                options={[
                  {
                    value: '[Genre]',
                    label: <Flex align="center">{(new Date()).getMilliseconds() % 2 === 0 ? <ManOutlined style={{ marginRight: 6 }} /> : <WomanOutlined style={{ marginRight: 6 }} />} Prospect - Genre</Flex>,
                  },
                  {
                    value: '[Prénom du prospect]',
                    label: <Flex align="center"><UserOutlined style={{ marginRight: 6 }} /> Prospect - Prénom</Flex>,
                  },
                  {
                    value: '[Nom de famille du prospect]',
                    label: <Flex align="center"><UserOutlined style={{ marginRight: 6 }} /> Prospect - Nom de famille</Flex>,
                  },
                  {
                    value: '[Téléphone du prospect]',
                    label: <Flex align="center"><PhoneOutlined style={{ marginRight: 6 }} /> Prospect - Téléphone</Flex>,
                  },
                  {
                    value: "[Email du prospect]",
                    label: <Flex align="center"><MailOutlined style={{ marginRight: 6 }} /> Prospect - Email</Flex>,
                  },
                  {
                    value: "[Contexte du prospect]",
                    label: <Flex align="center"><FileTextOutlined style={{ marginRight: 6 }} /> Prospect - Contexte</Flex>,
                  },
                  {
                    value: "[Prénom de l'IA]",
                    label: <Flex align="center"><RobotOutlined style={{ marginRight: 6 }} /> AI - Prénom</Flex>,
                  },
                  {
                    value: "[Fonction de l'IA]",
                    label: <Flex align="center"><RobotOutlined style={{ marginRight: 6 }} /> AI - Fonction</Flex>,
                  },
                  {
                    value: "[Nom de l'entreprise]",
                    label: <Flex align="center"><BankOutlined style={{ marginRight: 6 }} /> Client - Nom de l'entreprise</Flex>,
                  },
                  {
                    value: '[Nom du conseiller attitré]',
                    label: <Flex align="center"><TeamOutlined style={{ marginRight: 6 }} /> Conseiller - Nom et prénom</Flex>,
                  }
                ]}
                onChange={(value: string) => {
                  handleChange(index, { message: value });
                }}
                disabled={isBusy || disabled}
                placeholder="Exemple de message"
                autoSize={{ minRows: 4, maxRows: 8 }}
              />
            </Space>
          </Card>
        ))}
        <Button
          type="dashed"
          onClick={handleAdd}
          icon={<PlusOutlined />}
          disabled={isBusy || disabled}
          className="add-sample-button"
        >
          Ajouter un message d'exemple
        </Button>
      </div>
    </div>
  );
};

export default ScenarioSamplesEditor;
